/**
 * Shared constants for the Page Editor
 */
import React from "react";
import { Container } from "../blocks/Container";
import { Text } from "../blocks/Text";
import { Button } from "../blocks/Button";
import { Image } from "../blocks/Image";
import { Hero } from "../blocks/Hero";
import { ServiceCard } from "../blocks/ServiceCard";
import { Services } from "../blocks/Services";
import { Doctors } from "../blocks/Doctors";
import { DoctorCard } from "../blocks/DoctorCard";
import { ContactInfo } from "../blocks/ContactInfo";
import { Navbar } from "../blocks/Navbar";
import { Footer } from "../blocks/Footer";
import { Testimonial } from "../blocks/Testimonial";
import { Divider } from "../blocks/Divider";
import { Video } from "../blocks/Video";
import { FAQ } from "../blocks/FAQ";
import { Gallery } from "../blocks/Gallery";
import { GoogleMap } from "../blocks/GoogleMap";
import { SocialLinks } from "../blocks/SocialLinks";
import { FloatingCTA } from "../blocks/FloatingCTA";
import { HeroSlider } from "../blocks/HeroSlider";
import { BeforeAfter } from "../blocks/BeforeAfter";
import { PromotionBanner } from "../blocks/PromotionBanner";
// Flexible Navbar blocks
import { NavbarContainer } from "../blocks/NavbarContainer";
import { NavLogo } from "../blocks/NavLogo";
import { NavMenu } from "../blocks/NavMenu";
import { NavCTA } from "../blocks/NavCTA";
import { FlexNavbar } from "../blocks/FlexNavbar";
import { PricingCard } from "../blocks/PricingCard";
import { FeatureCard } from "../blocks/FeatureCard";
import { FilterableFeatures } from "../blocks/FilterableFeatures";

// Component categories for toolbox
export const componentCategories = [
  {
    name: "Layout",
    icon: "⬜",
    components: [
      { name: "Container", icon: "⬜", element: React.createElement(Container) },
      { name: "Divider", icon: "➖", element: React.createElement(Divider) },
    ],
  },
  {
    name: "Content",
    icon: "📝",
    components: [
      { name: "Text", icon: "📝", element: React.createElement(Text, { text: "Add your text here" }) },
      { name: "Button", icon: "🔘", element: React.createElement(Button, { text: "Click me" }) },
      { name: "Image", icon: "🖼️", element: React.createElement(Image, { src: "", alt: "Image" }) },
      { name: "Video", icon: "🎬", element: React.createElement(Video, { videoUrl: "" }) },
    ],
  },
  {
    name: "Sections",
    icon: "📦",
    components: [
      {
        name: "Hero",
        icon: "🏠",
        element: React.createElement(Hero, { title: "Welcome", subtitle: "Your subtitle here" }),
      },
      { name: "Slider", icon: "🎠", element: React.createElement(HeroSlider, { slides: [] }) },
      { name: "Promo", icon: "🏷️", element: React.createElement(PromotionBanner, { title: "Special Offer!" }) },
      { name: "Navbar", icon: "🧭", element: React.createElement(Navbar) },
      { name: "Footer", icon: "📋", element: React.createElement(Footer, { clinicName: "Clinic", columns: [] }) },
    ],
  },
  {
    name: "Clinic",
    icon: "🏥",
    components: [
      {
        name: "Services",
        icon: "🏥",
        element: React.createElement(Services),
      },
      {
        name: "Service",
        icon: "💊",
        element: React.createElement(ServiceCard, { title: "Service", description: "Description" }),
      },
      {
        name: "Doctors",
        icon: "👨‍⚕️",
        element: React.createElement(Doctors),
      },
      {
        name: "Doctor",
        icon: "🩺",
        element: React.createElement(DoctorCard),
      },
      {
        name: "Review",
        icon: "💬",
        element: React.createElement(Testimonial, { quote: "Great service!", authorName: "Customer" }),
      },
      {
        name: "Before/After",
        icon: "🔄",
        element: React.createElement(BeforeAfter, { beforeImage: "", afterImage: "" }),
      },
      {
        name: "Contact",
        icon: "📞",
        element: React.createElement(ContactInfo, {
          clinicName: "Clinic",
          phone: "02-xxx-xxxx",
          email: "email@clinic.com",
          address: "Address",
        }),
      },
      { name: "Map", icon: "🗺️", element: React.createElement(GoogleMap) },
    ],
  },
  {
    name: "Extra",
    icon: "🌐",
    components: [
      { name: "Social", icon: "🔗", element: React.createElement(SocialLinks) },
      { name: "Gallery", icon: "🖼️", element: React.createElement(Gallery) },
      {
        name: "FAQ",
        icon: "❓",
        element: React.createElement(FAQ, { items: [{ question: "Question?", answer: "Answer" }] }),
      },
      { name: "CTA", icon: "💬", element: React.createElement(FloatingCTA) },
      { name: "Pricing", icon: "💰", element: React.createElement(PricingCard) },
      { name: "FeatureGrid", icon: "🔖", element: React.createElement(FilterableFeatures) },
      { name: "FeatureCard", icon: "✨", element: React.createElement(FeatureCard) },
    ],
  },
  {
    name: "Nav+",
    icon: "🧩",
    components: [
      { name: "FlexNavbar", icon: "🧭", element: React.createElement(FlexNavbar) },
      { name: "NavContainer", icon: "📦", element: React.createElement(NavbarContainer) },
      { name: "NavLogo", icon: "🏷️", element: React.createElement(NavLogo, { logoText: "Logo" }) },
      { name: "NavMenu", icon: "📋", element: React.createElement(NavMenu) },
      { name: "NavCTA", icon: "🔘", element: React.createElement(NavCTA, { text: "Book Now" }) },
      // { name: 'LINE Login', icon: '🟢', element: React.createElement(LineLoginButton) }, // Hidden for now
    ],
  },
];

// Icon based on component type
export const componentIcons: Record<string, string> = {
  RootContainer: "📄",
  Container: "⬜",
  Text: "📝",
  Button: "🔘",
  Image: "🖼️",
  Hero: "🏠",
  HeroSlider: "🎠",
  BeforeAfter: "🔄",
  PromotionBanner: "🏷️",
  Navbar: "🧭",
  Footer: "📋",
  ServiceCard: "💊",
  Services: "🏥",
  Doctors: "👨‍⚕️",
  DoctorCard: "🩺",
  Testimonial: "💬",
  ContactInfo: "📞",
  ContactForm: "📧",
  GoogleMap: "🗺️",
  SocialLinks: "🔗",
  Gallery: "🖼️",
  FAQ: "❓",
  Video: "🎬",
  Divider: "➖",
  FloatingCTA: "💬",
  // Flexible Navbar blocks
  NavbarContainer: "📦",
  NavLogo: "🏷️",
  NavMenu: "📋",
  NavCTA: "🔘",
  LineLoginButton: "🟢",
  PricingCard: "💰",
  FeatureCard: "✨",
  FilterableFeatures: "🔖",
};

// Component descriptions for toolbox
export const componentDescriptions: Record<string, string> = {
  Container: "Group elements together",
  Divider: "Separate sections",
  Text: "Add text content",
  Button: "Clickable button",
  Image: "Display an image",
  Video: "Embed video",
  Hero: "Large header section",
  Slider: "Image carousel",
  Promo: "Promotion banner",
  Navbar: "Navigation menu",
  Footer: "Page footer",
  Service: "Service card",
  Services: "Service carousel",
  Doctors: "Doctor carousel",
  Doctor: "Single doctor card",
  Review: "Customer review",
  "Before/After": "Compare images",
  Contact: "Contact details",
  Map: "Google Maps",
  Social: "Social media links",
  Gallery: "Image gallery",
  FAQ: "FAQ accordion",
  CTA: "Floating action button",
  // Flexible Navbar blocks
  NavContainer: "Flexible navbar container",
  NavLogo: "Logo for navbar",
  NavMenu: "Menu container",
  NavCTA: "Call-to-action button",
  "LINE Login": "LINE LIFF login button",
  Pricing: "Pricing plan card",
  FeatureCard: "Drop into FeatureGrid",
  FeatureGrid: "Filterable feature grid with tabs",
};

// Clipboard for copy/paste (module-level)
export let clipboardData: { type: string; props: Record<string, unknown> } | null = null;

export const setClipboardData = (data: { type: string; props: Record<string, unknown> } | null) => {
  clipboardData = data;
};
